/**
 * shared/weapons/missile.ts — missile launch + flight step, shared by the server (sim/combat) and the client's
 * prediction of its own projectiles (client/net/projectiles.ts). Pure + deterministic: dsin/dcos only.
 *
 * OWNER: WEAPONS agent.
 *
 * Sources (T5 = BO1 decompile tools/src/KisakBlack, ESTIMATE for T6 [WPN §0]):
 *  - bolt / ballistic knife: FireWeapon -> Weapon_RocketLauncher_Fire -> G_FireRocket (g_weapon.cpp:694-728,
 *    g_missile.cpp:5369): start = view origin (G_CalcMuzzlePoints -> G_GetPlayerViewOrigin), direction = view
 *    forward jittered inside a disc of radius tan(spread)*16 at 16 units (gunrandom), velocity =
 *    projectileSpeed*dir + (0,0,projectileSpeedUp), gunVel = vec3_origin (no inherited velocity);
 *    guidedMissileType Ballistic -> TR_GRAVITY with bg_gravity 800 [WPN §2.1, §3].
 *  - spread: BG_GetSpreadForWeapon (bg_weapons.cpp:406, viewHeight-interpolated stand/ducked/prone min/max)
 *    and FireWeapon's lerp(min, max, scale) / lerp(adsSpread, max, scale) at full ADS (g_weapon.cpp:1153).
 *  - hatchet: G_UseOffHand -> Weapon_Throw_Grenade (g_weapon.cpp:578): 1600*forward + 0*relativeUp
 *    + (0,0,40) + 150*horizontalForward, truncated to integers (G_InitGrenadeMovement), plus the player's
 *    velocity along the throw direction [WPN §4]. Blade rotation: pitch starts at angle-120, 1500 deg/s.
 *  - flight: G_RunMissileInternal (g_missile.cpp:1096): point trace (G_LocationalTrace) from the current origin
 *    to the trajectory point, passEntityNum = owner for the WHOLE flight (T5 never hits its own thrower; the
 *    brief's "first 100 ms" rule is replaced by the engine's behaviour). Mask MASK_MISSILESHOT.
 *  - hatchet bounce: BounceMissile + GrenadeBounceVelocity (non-rolling branch, g_missile.cpp:2162/2750):
 *    reflect at the impact velocity, then v = par*v' + (per-par)(v'.n)n with the per-surface tables; at rest
 *    when n.z > 0.7 and |v| < grenadeRestThreshold 20 (T6 dvar, data/dvars_t6mp.json).
 */
import { MISSILE_GRAVITY } from '../constants';
import { DEG2RAD, angleNormalize360, angleVectors, dcos, dsin, hashRandom, vectorToAngles, type Vec3 } from '../math';
import { MissileKind, MissileState, WeaponId, type PlayerState } from '../types';
import { MASK_MISSILESHOT, SURFACE_TYPE_FLESH, createTraceResult } from '../collision/types';
import { getWeaponDef, type WeaponDef } from './defs';
import type { ComputeMissileLaunchFn, MissileSim, MissileStepFn } from './types';

/** grenadeRestThreshold 20 (T6 dvars_t6mp.json; T5 g_missile.cpp:297). */
export const GRENADE_REST_THRESHOLD = 20;
/** T5 G_InitGrenadeMovement: WEAPROTATE_BLADE_ROTATE spins the pitch at 1500 deg/s. */
export const BLADE_SPIN_DEG_PER_S = 1500;
/** Clients are < 32 in T5; we treat entityNum < this as a player (MAX_CLIENTS is 8, any value <= 0x3fd works). */
const PLAYER_ENT_LIMIT = 64;

const ZERO: Vec3 = [0, 0, 0];
const fwd: Vec3 = [0, 0, 0];
const right: Vec3 = [0, 0, 0];
const up: Vec3 = [0, 0, 0];

export function missileKindOf(weapon: WeaponId): MissileKind {
  return weapon === WeaponId.HATCHET ? MissileKind.HATCHET : weapon === WeaponId.KNIFE_BALLISTIC ? MissileKind.BALLISTIC_KNIFE : MissileKind.BOLT;
}

/**
 * BG_GetSpreadForWeapon + FireWeapon's lerp: the hip/ADS spread cone (degrees) for a shot fired with
 * `scale01` = aimSpreadScale/255 sampled before the shot (ps.fireSpreadScale).
 */
export function weaponSpreadDeg(def: WeaponDef, viewHeight: number, adsFrac: number, scale01: number): number {
  let min: number, max: number;
  if (viewHeight <= 40) {
    const f = (viewHeight - 11) / 29;
    min = (def.hipSpreadDuckedMin - def.hipSpreadProneMin) * f + def.hipSpreadProneMin;
    max = (def.hipSpreadDuckedMax - def.hipSpreadProneMax) * f + def.hipSpreadProneMax;
  } else {
    const f = (viewHeight - 40) / 20;
    min = (def.hipSpreadStandMin - def.hipSpreadDuckedMin) * f + def.hipSpreadDuckedMin;
    max = (def.hipSpreadMax - def.hipSpreadDuckedMax) * f + def.hipSpreadDuckedMax;
  }
  if (adsFrac === 1) return (max - def.adsSpread) * scale01 + def.adsSpread;
  return (max - min) * scale01 + min;
}

/** Launch parameters from the shooter's (post-pmove) PlayerState; identical on server and client. */
export const computeMissileLaunch: ComputeMissileLaunchFn = (ps: PlayerState, weapon: WeaponId) => {
  const def = getWeaponDef(weapon);
  angleVectors(ps.viewangles, fwd, right, up);
  const origin: Vec3 = [ps.origin[0], ps.origin[1], ps.origin[2] + ps.viewHeightCurrent];
  const velocity: Vec3 = [0, 0, 0];
  const kind = missileKindOf(weapon);
  const gravity = MISSILE_GRAVITY; // Ballistic / grenade: TR_GRAVITY, bg_gravity 800 (T5 bg_misc.cpp:1564)
  if (weapon === WeaponId.HATCHET) {
    // Weapon_Throw_Grenade
    for (let i = 0; i < 3; i++) velocity[i] = def.projectileSpeed * fwd[i] + def.projectileSpeedRelativeUp * up[i];
    velocity[2] += def.projectileSpeedUp;
    if (def.projectileSpeedForward) {
      const hl = Math.sqrt(fwd[0] * fwd[0] + fwd[1] * fwd[1]);
      if (hl > 0) {
        velocity[0] += (def.projectileSpeedForward * fwd[0]) / hl;
        velocity[1] += (def.projectileSpeedForward * fwd[1]) / hl;
      }
    }
    // G_InitGrenadeMovement truncates trDelta to integers; then Weapon_Throw_Grenade adds the thrower's velocity
    // along the (untruncated) normalized throw direction: fAddVel = dot(ps.velocity, dir).
    const tossLen = Math.sqrt(velocity[0] * velocity[0] + velocity[1] * velocity[1] + velocity[2] * velocity[2]);
    const angles: Vec3 = [0, 0, 0];
    vectorToAngles(angles, velocity);
    const dx = tossLen > 0 ? velocity[0] / tossLen : 0, dy = tossLen > 0 ? velocity[1] / tossLen : 0, dz = tossLen > 0 ? velocity[2] / tossLen : 0;
    for (let i = 0; i < 3; i++) velocity[i] = Math.trunc(velocity[i]);
    const add = ps.velocity[0] * dx + ps.velocity[1] * dy + ps.velocity[2] * dz;
    velocity[0] += add * dx;
    velocity[1] += add * dy;
    velocity[2] += add * dz;
    if (def.rotate) angles[0] = angleNormalize360(angles[0] - 120); // blade style spin start
    return { kind, weapon, origin, velocity, angles, gravity };
  }
  // Weapon_RocketLauncher_Fire: spread disc at 16 units (gunrandom with deterministic seeds)
  const spread = weaponSpreadDeg(def, ps.viewHeightCurrent, ps.adsFrac, ps.fireSpreadScale / 255);
  const dir: Vec3 = [fwd[0] * 16, fwd[1] * 16, fwd[2] * 16];
  if (spread > 0) {
    const sr = spread * DEG2RAD;
    const aim = (dsin(sr) / dcos(sr)) * 16;
    const theta = hashRandom(ps.commandTime, ps.fireSeq) * 360 * DEG2RAD;
    const r = hashRandom(ps.fireSeq ^ 0x5bd1, ps.commandTime + ps.clientNum * 7919);
    const ru = r * dcos(theta) * aim, uu = r * dsin(theta) * aim;
    for (let i = 0; i < 3; i++) dir[i] += ru * right[i] + uu * up[i];
  }
  const len = Math.sqrt(dir[0] * dir[0] + dir[1] * dir[1] + dir[2] * dir[2]);
  for (let i = 0; i < 3; i++) velocity[i] = (def.projectileSpeed * dir[i]) / len;
  velocity[2] += def.projectileSpeedUp;
  const angles: Vec3 = [0, 0, 0];
  vectorToAngles(angles, velocity);
  return { kind, weapon, origin, velocity, angles, gravity };
};

const tr = createTraceResult();
const next: Vec3 = [0, 0, 0];
const vHit: Vec3 = [0, 0, 0];

/**
 * Advance one flying missile by dtMs (any step; the path is the exact parabola). On a hit, the missile stops at
 * the impact point (out.hit, bounced=false); the caller decides sticking/damage. A hatchet hitting the WORLD
 * bounces (out.bounced=true, keeps flying) or comes to rest (out.hit=true, bounced=false, entity = world,
 * velocity zeroed). A hatchet hitting a player stops like the others (the server sticks it / drops it).
 */
export const missileStep: MissileStepFn = (m, dtMs, trace, _nowMs, out) => {
  const dt = dtMs * 0.001;
  next[0] = m.origin[0] + m.velocity[0] * dt;
  next[1] = m.origin[1] + m.velocity[1] * dt;
  next[2] = m.origin[2] + m.velocity[2] * dt - 0.5 * m.gravity * dt * dt;
  trace(tr, m.origin, next, ZERO, ZERO, MASK_MISSILESHOT, m.owner);
  out.bounced = false;
  out.hit = tr.fraction < 1 || tr.startsolid;
  const frac = tr.startsolid ? 0 : tr.fraction;
  if (!out.hit) {
    m.origin[0] = next[0]; m.origin[1] = next[1]; m.origin[2] = next[2];
    m.velocity[2] -= m.gravity * dt;
    out.entityNum = tr.entityNum;
    updateFlightAngles(m, dt);
    out.pos[0] = m.origin[0]; out.pos[1] = m.origin[1]; out.pos[2] = m.origin[2];
    return out;
  }
  // impact
  m.origin[0] = tr.endpos[0]; m.origin[1] = tr.endpos[1]; m.origin[2] = tr.endpos[2];
  vHit[0] = m.velocity[0]; vHit[1] = m.velocity[1]; vHit[2] = m.velocity[2] - m.gravity * dt * frac;
  out.entityNum = tr.entityNum;
  out.pos[0] = m.origin[0]; out.pos[1] = m.origin[1]; out.pos[2] = m.origin[2];
  out.normal[0] = tr.normal[0]; out.normal[1] = tr.normal[1]; out.normal[2] = tr.normal[2];
  if (tr.startsolid && out.normal[0] === 0 && out.normal[1] === 0 && out.normal[2] === 0) {
    // G_MissileTrace: startsolid -> normal = -direction
    const l = Math.sqrt(vHit[0] * vHit[0] + vHit[1] * vHit[1] + vHit[2] * vHit[2]) || 1;
    out.normal[0] = -vHit[0] / l; out.normal[1] = -vHit[1] / l; out.normal[2] = -vHit[2] / l;
  }
  const isPlayer = tr.entityNum >= 0 && tr.entityNum < PLAYER_ENT_LIMIT;
  out.surfaceType = isPlayer ? SURFACE_TYPE_FLESH : tr.surfaceType; // T5: client hit without sflags -> flesh (0x700000)
  const def = getWeaponDef(m.weapon);
  if (def.stickiness === 'Stick to flesh' && !isPlayer) {
    // BounceMissile / GrenadeBounceVelocity (non-rolling)
    const n = out.normal;
    const dot = vHit[0] * n[0] + vHit[1] * n[1] + vHit[2] * n[2];
    const rx = vHit[0] - 2 * dot * n[0], ry = vHit[1] - 2 * dot * n[1], rz = vHit[2] - 2 * dot * n[2];
    const st = out.surfaceType;
    const par = def.parallelBounce[st] ?? def.parallelBounce[0];
    const per = def.perpendicularBounce[st] ?? def.perpendicularBounce[0];
    const tn = (per - par) * (rx * n[0] + ry * n[1] + rz * n[2]);
    m.velocity[0] = tn * n[0] + par * rx;
    m.velocity[1] = tn * n[1] + par * ry;
    m.velocity[2] = tn * n[2] + par * rz;
    const speed = Math.sqrt(m.velocity[0] * m.velocity[0] + m.velocity[1] * m.velocity[1] + m.velocity[2] * m.velocity[2]);
    if (n[2] > 0.7 && speed < GRENADE_REST_THRESHOLD) {
      // stationary (MissileLandAngles: lies flat on the surface)
      m.velocity[0] = m.velocity[1] = m.velocity[2] = 0;
      m.angles[0] = 0;
      m.angles[2] = 0;
      return out; // hit, not bounced: at rest
    }
    out.bounced = true;
    out.hit = true;
    return out;
  }
  m.velocity[0] = vHit[0]; m.velocity[1] = vHit[1]; m.velocity[2] = vHit[2];
  if (def.projExplosionType === 'bolt') vectorToAngles(m.angles, m.velocity);
  return out;
};

/** Bolt/knife (projExplosionType bolt): nose follows the velocity (T5 g_missile.cpp:1213); hatchet: blade spin. */
function updateFlightAngles(m: MissileSim, dt: number): void {
  const def = getWeaponDef(m.weapon);
  if (def.projExplosionType === 'bolt') vectorToAngles(m.angles, m.velocity);
  else if (def.rotate) m.angles[0] = angleNormalize360(m.angles[0] + BLADE_SPIN_DEG_PER_S * dt);
}

/** Convenience for tests / callers: a MissileSim from a launch. */
export function missileSimFromLaunch(ps: PlayerState, weapon: WeaponId, spawnTime: number): MissileSim {
  const l = computeMissileLaunch(ps, weapon);
  return {
    kind: l.kind, weapon: l.weapon, owner: ps.clientNum, origin: l.origin, velocity: l.velocity, angles: l.angles,
    gravity: l.gravity, state: MissileState.FLYING, spawnTime,
  };
}
